import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { getErrorDetails } from "../../app/appUtils";
import AppButton from "../../components/ui/AppButton";
import { Spinner } from "../../components/ui/common";
import { Modal } from "../../components/ui/Modal";
import SuccessView from "../../components/ui/SuccessView";
import { useToast } from "../../hooks/useToast";
import DriverDropdownMenu from "../driver/ui/DriverDropdownMenu";
import { FineHistory } from "./@types";
import {
  useCreateFineHistoryMutation,
  useUpdateFineHistoryMutation,
} from "./fineApiSlice";
import FineDropdownMenu from "./ui/FineDropdownMenu";

export const issueFineFormSchema = z.object({
  fineId: z.string().nonempty("Fine is required"),
  driverId: z.string().nonempty("Driver is required"),
});

export type IssueFineFormData = z.infer<typeof issueFineFormSchema>;

interface IssueFineModelProps {
  isOpen: boolean;
  onClose: () => void;
  issueFine?: FineHistory | null;
  mode?: "add" | "edit";
}

const IssueFineModal: React.FC<IssueFineModelProps> = ({
  isOpen,
  onClose,
  issueFine = null,
  mode = issueFine ? "edit" : "add",
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const toast = useToast();
  const isEditMode = mode === "edit" && issueFine;

  const [
    createFineHistory,
    {
      isLoading: isCreating,
      isSuccess: createSuccess,
      isError: createError,
      error: createErrorData,
      reset: resetCreate,
    },
  ] = useCreateFineHistoryMutation();

  const [
    updateFineHistory,
    {
      isLoading: isUpdating,
      isSuccess: updateSuccess,
      isError: updateError,
      error: updateErrorData,
      reset: resetUpdate,
    },
  ] = useUpdateFineHistoryMutation();

  // Derived states for cleaner code
  const isLoading = isCreating || isUpdating;
  const isSuccess = createSuccess || updateSuccess;
  const isError = createError || updateError;
  const error = createErrorData || updateErrorData;

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    watch,
    setValue,
    reset,
  } = useForm<IssueFineFormData>({
    resolver: zodResolver(issueFineFormSchema),
    mode: "onChange",
    defaultValues: {
      fineId: "",
      driverId: "",
    },
  });

  // Populate form with issueFine data when editing
  useEffect(() => {
    if (isEditMode && issueFine) {
      reset({
        fineId: issueFine.fineId,
        driverId: issueFine.driverId,
      });
    } else if (!isEditMode) {
      // Reset to empty when switching to add mode
      reset({
        fineId: "",
        driverId: "",
      });
    }
  }, [isEditMode, issueFine, reset]);

  const onSubmit = async (data: IssueFineFormData) => {
    if (isSubmitting || isLoading) return;

    // Prevent submission if editing and no changes were made
    if (isEditMode && !isDirty) {
      toast.warning({
        title: "No Changes",
        message: "No changes were made to update.",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        fineId: data.fineId,
        driverId: data.driverId,
      };

      if (isEditMode && issueFine?.id) {
        await updateFineHistory({
          id: issueFine.id,
          ...payload,
        }).unwrap();
      } else {
        await createFineHistory(payload).unwrap();
      }

      setShowSuccess(true);
    } catch (err) {
      console.error(`${isEditMode ? "Update" : "Create"} error:`, err);
      const errorMessage =
        getErrorDetails(err).message ||
        `Failed to ${
          isEditMode ? "update" : "add"
        } issueFine. Please try again.`;

      toast.error({
        title: "Error",
        message: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (isSuccess && !showSuccess) {
      toast.success({
        title: "Success",
        message: `Fine ${isEditMode ? "updated" : "issued"} successfully`,
      });
      setShowSuccess(true);
    }

    if (isError) {
      const errorMessage =
        getErrorDetails(error).message ||
        `Failed to ${
          isEditMode ? "update" : "add"
        } issueFine. Please try again.`;

      toast.error({
        title: "Error",
        message: errorMessage,
      });
    }
  }, [isSuccess, isError, error, toast, isEditMode, showSuccess]);

  const handleCloseModal = () => {
    setShowSuccess(false);
    reset();
    resetCreate();
    resetUpdate();
    onClose();
  };

  const renderSuccessView = () => {
    if (showSuccess) {
      return (
        <SuccessView
          handleCloseModal={handleCloseModal}
          heading=""
          message={`Fine has been ${
            isEditMode ? "updated" : "issued"
          } successfully.`}
        />
      );
    }
    return null;
  };

  const modalTitle = showSuccess
    ? ""
    : `${isEditMode ? "Edit issued" : "Issue"} fine`;
  const submitButtonText = isEditMode ? "Update issued fine" : "+ Issue fine";

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCloseModal}
      title={modalTitle}
      className="max-w-full sm:max-w-[80vw] lg:max-w-[45vw] w-full bg-white"
    >
      {isSuccess ? (
        renderSuccessView()
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 p-6">
          <div className="">
            <Controller
              control={control}
              name="fineId"
              render={({ field }) => (
                <FineDropdownMenu
                  value={watch("fineId")}
                  onChange={(value) =>
                    setValue("fineId", value, {
                      shouldValidate: true,
                    })
                  }
                  className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px] mb-6"
                  error={errors.fineId?.message}
                  placeholder="Select a fine"
                />
              )}
            />
          </div>

          <div className="">
            <Controller
              control={control}
              name="driverId"
              render={({ field }) => (
                <DriverDropdownMenu
                  value={watch("driverId")}
                  onChange={(value) =>
                    setValue("driverId", value, {
                      shouldValidate: true,
                    })
                  }
                  className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px] mb-6"
                  error={errors.driverId?.message}
                  placeholder="Select a driver"
                />
              )}
            />
          </div>

          <div className="flex justify-center items-center space-x-4 text-[15px]">
            <AppButton
              type="button"
              onClick={handleCloseModal}
              disabled={isSubmitting || isLoading}
              className="px-6 py-2 bg-[#F6F6F6] rounded-md text-hColor hover:bg-gray-50 transition-colors text-[15px] disabled:opacity-50"
            >
              Cancel
            </AppButton>

            <AppButton
              type="submit"
              disabled={isSubmitting || isLoading}
              className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary/75 transition-colors disabled:opacity-50 text-[15px]"
            >
              {isSubmitting || isLoading ? (
                <>
                  <Spinner /> Adding
                </>
              ) : (
                submitButtonText
              )}
            </AppButton>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default IssueFineModal;
